import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { StateParams } from "../../common";
import { ICommonQueryingReport } from "./commonQueryingReport";
import { Plant } from "../../models/navigation";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import template from './cavityCorrelationReport.tpl.html';
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import CountsServiceName, { CountsService } from "../../services/counts/counts.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import UIUtilityServiceName, { UIUtilityService } from "../../services/utility/uiUtility.service";
import NavigationServiceName, { NavigationService } from "../../services/navigation/navigation.service";
import { States } from "../../shared/config/constant.module";
import Graph from './cavityCorrelationSummaryGraph.directive';
import * as Highcharts from 'highcharts/highstock';

let requires = [LoaderServiceName, CountsServiceName, UIUtilityServiceName, NavigationServiceName, Graph];

function genericBarChart() {
  return {
    restrict: 'E',
    template: '<div></div>',
    scope: {
      options: '='
    },
    link: function (scope, element, attributes) {
      angular.element(document).ready(function () {
        // Load any charts with data (first time)
        try {
          let jsonData = JSON.parse(attributes.data);
          if (attributes.data !== null && jsonData.length >= 1 && attributes.data !== '[0,0,0,0]') {
            scope.options.series[0].data = jsonData;
            let chart = new Highcharts.Chart(element[0], scope.options);
            chart.redraw();
          }
        } catch (e) {
          return false;
        }
        // watch the data attribute and if it changes, update the chart series
        scope.$watch(function () {
          return element.attr('data');
        }, function (newValue) {
          try {
            let jsonData = JSON.parse(attributes.data);
            if (newValue !== null && jsonData.length >= 1 && newValue !== '[0,0,0,0]') {
              scope.options.series[0].data = jsonData;
              let chart = new Highcharts.Chart(element[0], scope.options);
              chart.redraw();
            }
          } catch (e) {
            return false;
          }
        });
      });
    }
  };
}

export interface CavityCorrelationReportParams extends StateParams {
  reportPlantId: string;
  reportShopId: string;
  startDate: string;
  endDate: string;
  autoRun: boolean;
  cavity: string;
}

interface CavityCorrelationReportController extends ICommonQueryingReport {
  plants: Plant[];
  selectedPlant: Plant;
  selectedShop: apsViewModels.ShopNavigation;
  startDate: Date;
  endDate: Date;
  uiStartDate: Date;
  uiEndDate: Date;

  reportStartDate: Date;
  reportEndDate: Date;
  reportShopId: string;
  reportShopName: string;
  reportTimeZoneId: string;

  form: angular.IFormController;

  updatePlant(): void;
  formatTime(time: Date): string;
  generateReport(): void;
  generateDetailReport(summary: apsViewModels.CavityCorrelationCountSummary): void;

  cavities: apsViewModels.CavityCorrelationCountSummary[];
  searchPerformed: boolean;

  scopeWorkaround: {
    cavityBarClick(summary: apsViewModels.CavityCorrelationCountSummary): void;
  };

  requirePlantSelection: boolean;
  requireShopSelection: boolean;

  correlationChartConfig: any;
  defectData: any;
  selectedCavity: any;
  showCharts: boolean;
}

class CavityCorrelationReportCtrl implements CavityCorrelationReportController {
  plants: Plant[];
  selectedPlant: Plant;
  selectedShop: apsViewModels.ShopNavigation;
  startDate: Date;
  endDate: Date;
  uiStartDate: Date;
  uiEndDate: Date;

  reportStartDate: Date;
  reportEndDate: Date;
  reportShopId: string;
  reportShopName: string;
  reportTimeZoneId: string;

  form: angular.IFormController;

  cavities: apsViewModels.CavityCorrelationCountSummary[];
  searchPerformed: boolean;

  detailGraphHeight: string;
  detailHorizontalBarHeight: number;

  loaderService: LoaderService;
  countsService: CountsService;
  uiUtilityService: UIUtilityService;
  $anchorScroll: angular.IAnchorScrollService;
  $location: angular.ILocationService;
  $state: angular.ui.IStateService;
  $stateParams: CavityCorrelationReportParams;
  private $timeout: angular.ITimeoutService;

  scopeWorkaround: {
    cavityBarClick(summary: apsViewModels.CavityCorrelationCountSummary): void;
  };

  requirePlantSelection: boolean;
  requireShopSelection: boolean;

  correlationChartConfig: any;
  defectData: any;
  selectedCavity: any;
  showCharts: boolean;

  private $q: angular.IQService;

  constructor(navigationService: NavigationService,
              countsService: CountsService,
              uiUtilityService: UIUtilityService,
              loaderService: LoaderService,
              $anchorScroll: angular.IAnchorScrollService,
              $location: angular.ILocationService,
              $state: angular.ui.IStateService,
              $q: angular.IQService,
              $stateParams: CavityCorrelationReportParams,
              $timeout: angular.ITimeoutService) {

    this.loaderService = loaderService;
    this.countsService = countsService;
    this.uiUtilityService = uiUtilityService;
    this.$anchorScroll = $anchorScroll;
    this.$location = $location;
    this.$state = $state;
    this.$q = $q;
    this.$stateParams = $stateParams;
    this.$timeout = $timeout;

    this.requirePlantSelection = true;
    this.requireShopSelection = true;

    this.detailGraphHeight = '500px';
    this.detailHorizontalBarHeight = 40;

    let now = moment().add(1, 'hours').startOf('hour');
    this.uiEndDate = now.toDate();
    this.uiStartDate = now.clone().subtract(1, 'days').toDate();

    let promise = navigationService.getNavigation().then((nav) => {
      this.plants = nav.plants();
      this.selectedPlant = nav.selectedPlant;
      this.updatePlant();
      this.selectedShop = nav.selectedShop;

      return this.loadState($stateParams);
    });

    loaderService.loadingPromise(promise);

    this.scopeWorkaround = {
      cavityBarClick: (summary: apsViewModels.CavityCorrelationCountSummary) => {
        this.showCharts = false;
        this.generateDetailReport(summary);
      }
    };
  }

  getCorrelationChart() {
    return {
      chart: {
        type: 'bar',
        height: `${(100 + this.defectData.length * this.detailHorizontalBarHeight)}px`
      },
      credits: {
        enabled: false
      },
      yAxis: {
        title: {
          enabled: false
        }
      },
      xAxis: {
        type: 'category'
      },
      title: {
        text: 'Defects for Cavity'
      },
      legend: {
        enabled: false
      },
      series: [{
        colorByPoint: true,
        name: 'Reject Count',
        data: this.defectData
      }]
    };
  }

  generateReport(): angular.IPromise<any> {
    if (this.form.$invalid) {
      return;
    }

    this.saveState(undefined);

    let promise = this.runReport();
    this.loaderService.loadingPromise(promise);
    return promise;
  }

  private runReport(): angular.IPromise<any> {
    this.reportShopId = this.selectedShop.id;
    this.reportShopName = this.selectedShop.name;
    this.reportStartDate = this.startDate;
    this.reportEndDate = this.endDate;
    this.reportTimeZoneId = this.selectedPlant.timeZoneId;

    this.cavities = [];
    this.searchPerformed = false;

    return this.countsService.getCavityCorrelation(this.reportShopId,
      this.reportStartDate,
      this.reportEndDate)
      .then((cavities) => {
        this.cavities = cavities;
        this.searchPerformed = true;
        return this.$q.when();
      });
  }

  formatTime(time: Date): string {
    return DateUtilityService.formatTime(time, this.reportTimeZoneId);
  }

  updatePlant(): void {
    this.selectedShop = null;
  }

  generateDetailReport(summary: apsViewModels.CavityCorrelationCountSummary): void {
    let cavity = summary ? summary.cavity : '';
    this.saveState(cavity);
    this.runDetailReport(summary);
  }

  private runDetailReport(summary: apsViewModels.CavityCorrelationCountSummary): void {
    this.$timeout(() => {
      this.defectData = [];
      this.selectedCavity = {};
    });

    this.$timeout(() => {
      if (summary.details) {
        // set the defect info for the chart
        this.defectData = [];
        _.map(summary.details, detail => {
          let point = {
            name: detail.defect,
            y: detail.rejectCount
          };
          this.defectData.push(point);
        });
      }
      this.selectedCavity = summary;   
      this.correlationChartConfig = this.getCorrelationChart();
      this.showCharts = true;
      console.log('showcharts/summary', this.showCharts, this.selectedCavity);
    });
  }

  loadState(params: CavityCorrelationReportParams): angular.IPromise<any> {
    if (params.reportPlantId && params.reportShopId && params.reportShopId !== params.shopId) {
      let plant = _.find(this.plants, { id: params.reportPlantId });
      if (plant) {
        let shop = _.find(plant.shops, { id: params.reportShopId });
        if (shop) {
          this.selectedPlant = plant;
          this.selectedShop = shop;
        }
      }
    }

    DateUtilityService.setReportDates(this, params, this.$timeout);

    if (params.autoRun) {
      return this.runReport().then(() => {
        let targetSummary = _.find(this.cavities, { cavity: params.cavity });
        if (targetSummary) {
          this.runDetailReport(targetSummary);
          this.$location.hash('detailResultsMarker');
          this.$anchorScroll();
        }
      });
    }

    return this.$q.when();
  }

  saveState(cavity: string): void {
    let reportShopId = this.selectedShop ? this.selectedShop.id : undefined;
    if (reportShopId === this.$stateParams.shopId) {
      reportShopId = undefined;
    }
    let params: CavityCorrelationReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: (reportShopId && this.selectedPlant) ? this.selectedPlant.id : undefined,
      reportShopId: reportShopId,
      startDate: this.startDate ? this.startDate.toISOString() : undefined,
      endDate: this.endDate ? this.endDate.toISOString() : undefined,
      autoRun: true,
      cavity: cavity
    };

    this.$state.transitionTo(this.$state.current.name, params);
  }
}

export default angular.module('aps.controller.cavityCorrelationReport', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.CAVITY_CORRELATION,
      url: '/admin/reports/cavityCorrelation?startDate&endDate&autoRun&reportPlantId&reportShopId&cavity',
      params: {
        startDate: { dynamic: true },
        endDate: { dynamic: true },
        autoRun: { dynamic: true },
        reportPlantId: { dynamic: true },
        reportShopId: { dynamic: true },
        cavity: { dynamic: true }
      },
      controller: 'CavityCorrelationReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Cavity Correlation Report'
      }
    });
  })
  .controller('CavityCorrelationReportCtrl', CavityCorrelationReportCtrl)
  .directive('correlationChart', genericBarChart)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/reports/cavityCorrelationReport.controller.ts